/*
  hub-session.js — short-lived locker SESSION tokens (ADDON, removable)

  WHY THIS EXISTS (2026-08-01 penetration test, finding #4)
  --------------------------------------------------------
  The locker token used to be HMAC-SHA256(LOCKER_TOKEN_SECRET, customerId): deterministic, PERMANENT,
  no expiry, and revocable only by rotating the secret for every member at once. layout/theme.liquid
  rendered it into <body data-locker-token> on EVERY page, so anything running in the page — a
  third-party Shopify app script, a browser extension, an XSS, or a HAR file pasted into a support
  ticket — walked away with indefinite access to that member's locker, points and bookings.

  This module replaces it with a token that expires in 60 minutes and is fetched on demand.

  HOW THE BROWSER PROVES WHO IT IS WITHOUT HOLDING A SECRET
  --------------------------------------------------------
  It calls the Shopify App Proxy (settings.loyalty_proxy, e.g. /apps/loyalty). That path is
  same-origin, and Shopify itself signs the request and stamps it with `logged_in_customer_id`. The
  edge function verifies that signature and mints the session token. So the credential proving
  identity is produced by Shopify per-request and never stored in the page. That proxy path is not
  new or speculative — assets/hub-loyalty.js has authenticated every call through it in production
  for months.

  The mint route accepts NOTHING else — not a locker token (or one stolen permanent token would mint
  an endless supply of fresh ones and the expiry would mean nothing), and not the admin secret.

  TRANSITION SAFETY — read this before deleting anything
  ------------------------------------------------------
  This is additive and cannot lock anyone out:
    * The backend accepts BOTH shapes (v2 session tokens and the legacy permanent token) until
      LOCKER_LEGACY_TOKENS=0 is set on the edge functions.
    * If the mint fails for any reason — proxy misconfigured, offline, member logged out — every
      accessor below falls back to whatever layout/theme.liquid rendered, which is exactly today's
      behaviour.
    * On success the freshly minted token is written BACK into <body data-locker-token>, so the
      readers that were already reading that attribute at call time (hub-social.js, hub-profile.js,
      hub-draw.js) start using a short-lived token with no change to those files at all.

  THE REMAINING STEP, WHICH IS A THEME SETTING, NOT A DEPLOY:
    Theme settings -> Locker -> "Render locker token in page source" -> OFF.
    Do that once you have loaded the locker while logged in and confirmed it still works. Until then
    the permanent token is still in the page and the security benefit is only partial. Turning it off
    is instantly reversible and needs no code change.

  Load order note: this file is loaded BEFORE the modules that consume the token, and it starts the
  mint immediately, so `HubSession.ready` is already in flight by the time anything awaits it.
*/
(function () {
  'use strict';

  var B = document.body;
  var PROXY  = (B.getAttribute('data-loyalty-proxy') || '').trim();   // e.g. /apps/loyalty
  var MEMBER = (B.getAttribute('data-member-id') || '').trim();

  // Re-mint a minute before the real expiry so an in-flight request can never land expired.
  var EARLY_MS = 60000;

  var state = { token: '', expMs: 0, inflight: null, source: 'none' };
  // Bumped by refresh(). A mint that was already in the air when someone deliberately asked for a
  // NEW token is superseded: it may still answer its own caller, but it must not commit its result
  // over the fresher one. Without this, a 401-recovery re-mint can be silently undone by the very
  // request that failed.
  var gen = 0;
  var renewTimer = 0;

  function domToken() {
    try { return (B.getAttribute('data-locker-token') || '').trim(); } catch (e) { return ''; }
  }
  function fresh() { return !!state.token && (state.expMs - EARLY_MS) > Date.now(); }

  /* KEEP THE TOKEN ALIVE WITHOUT A RELOAD — the bug this closes, 2026-08-29.
     -------------------------------------------------------------------------------------------
     The backend mints with LOCKER_V2_TTL = 3600 and its own comment reads "the client re-mints on
     401, so this is a ceiling, not a session length". The client did no such thing: `refresh()` was
     written for exactly that job and had ZERO callers anywhere in the theme, and `token()` handed
     back `state.token` without ever consulting `fresh()`. So a locker tab left open for an hour —
     which is the normal way people use a browser — started returning a known-expired token to all
     seven surfaces that transmit one, and every write 401'd with nothing re-minting and, in most of
     them, nothing shown to the member.

     Two comments, on two sides, each correct about its own half, describing a handoff neither
     implemented. That is the same shape as the Strava outage the day before, which is why the fix
     is here in the shared module rather than repeated in seven consumers.

     A timer alone is not enough: setTimeout does not fire reliably across a laptop sleep or a
     throttled background tab, which is precisely when an hour passes. So the renewal is belt AND
     braces — a scheduled re-mint, a re-mint when a hidden tab becomes visible again, and ensure()
     below for callers that want to be certain at the moment they act. */
  function scheduleRenew() {
    try { clearTimeout(renewTimer); } catch (e) {}
    if (!state.expMs) return;
    var wait = state.expMs - EARLY_MS - Date.now();
    if (wait < 1000) wait = 1000;
    try { renewTimer = setTimeout(function () { mint(); }, wait); } catch (e) {}
  }

  /* ONE RETRY, for the transient case only. A single dropped request at page load used to leave
     `state.token` empty and `ready` permanently resolved to '' — every authenticated action in that
     tab then failed until the member happened to reload, because nothing ever re-attempted the mint.
     One retry turns the common blip into a non-event; the visibility handler and ensure() cover the
     rest, so this deliberately does not escalate into a retry storm. */
  function fetchSession(isRetry) {
    return fetch(PROXY + '?action=session', {
      credentials: 'same-origin',          // the App Proxy needs Shopify's customer session cookie
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function (e) {
        if (isRetry) throw e;
        return new Promise(function (res) { setTimeout(res, 1500); })
          .then(function () { return fetchSession(true); });
      });
  }

  function mint() {
    // No proxy or no logged-in member: nothing to mint. Fall back rather than fail — a logged-out
    // visitor legitimately has no token, and that is not an error.
    if (!PROXY || !MEMBER) return Promise.resolve(domToken());
    if (fresh()) return Promise.resolve(state.token);
    // Collapse concurrent callers onto ONE request. Several modules ask for a token during the same
    // page load; without this they would each mint a separate session and spend the per-member cap.
    if (state.inflight) return state.inflight;

    var myGen = gen;
    state.inflight = fetchSession(false)
      .then(function (j) {
        if (j && j.ok && j.token) {
          if (myGen === gen) {           // not superseded by a refresh() while this was in the air
            state.token  = j.token;
            state.expMs  = Date.parse(j.expires_at) || (Date.now() + 3000000);
            state.source = 'session';
            // Publish it where the call-time readers already look, so they need no changes.
            try { B.setAttribute('data-locker-token', j.token); } catch (e) {}
            try {
              document.dispatchEvent(new CustomEvent('hub:session-ready', { detail: { expires_at: j.expires_at } }));
            } catch (e) {}
            scheduleRenew();
          }
          // Superseded callers still get a usable token — prefer whatever is now committed, and fall
          // back to the one this request minted, which is itself only seconds old. Returning
          // domToken() here would hand back the empty string whenever the newer mint had not landed
          // yet, which is a worse answer than the perfectly good token we are holding.
          return (myGen === gen) ? j.token : (state.token || j.token);
        }
        if (myGen === gen) state.source = 'fallback';
        return domToken();
      })
      .catch(function () { if (myGen === gen) state.source = 'fallback'; return domToken(); })
      .then(function (t) { if (myGen === gen) state.inflight = null; return t; });

    return state.inflight;
  }

  // A tab that slept through its own expiry wakes up holding a dead token, and the timer above may
  // never have fired. Re-mint on the way back in — this also rescues a tab whose only mint failed,
  // which is the difference between "the locker recovered by itself" and "reload the page".
  try {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && !fresh()) mint();
    });
  } catch (e) {}

  window.HubSession = {
    // Resolves to the best token available. Await this before a FIRST authenticated request.
    // NOTE: this is a ONE-SHOT promise, settled once at page load — it is the right thing to await
    // during boot and the WRONG thing to await an hour later, because it will hand back the token it
    // resolved with (or the empty string, if that first mint failed). Use ensure() for anything that
    // happens in response to a member action.
    ready: mint(),
    // Best token available right now, synchronously. Falls back to the rendered attribute.
    token: function () { return state.token || domToken(); },
    // Promise of a token that is CURRENTLY valid, re-minting if the held one is stale, missing, or
    // was never obtained. Unlike `ready` this can be awaited any number of times, at any point in
    // the page's life, and it re-attempts a failed mint instead of replaying its failure.
    ensure: function () { return mint(); },
    // Force a new one — call this after a 401 so an expired session self-heals instead of the
    // member seeing a dead locker until they reload.
    refresh: function () {
      state.token = ''; state.expMs = 0;
      // Supersede any mint already in the air and drop the collapse slot, so this cannot be handed
      // back the very token that just got refused.
      gen++; state.inflight = null;
      return mint();
    },
    // For debugging in the console: 'session' means the permanent token is no longer in use.
    source: function () { return state.source; },
    // Exposed for the gate in _verify/locker-session-renewal-test.mjs and for console debugging:
    // false means the next authenticated call needs ensure() first.
    isFresh: function () { return fresh(); }
  };
})();
